/**
 * Determines if the current environment is a staging environment.
 * Checks the hostname for staging indicators.
 * 
 * @returns {boolean} True if running on staging, false otherwise
 */
import { CQAppConstant } from 'app-constants';

export const isStagingEnvironment = (): boolean => {
    if (typeof window === CQAppConstant.DATA_TYPES.UNDEFINED) {
        return false;
    }

    const hostname = window.location.hostname.toLowerCase();
    return hostname.includes(CQAppConstant.ENV_KEYWORDS.STAGING);
};

/**
 * Determines if a Salesforce URL points to a production organization.
 * Production URLs do NOT contain the word "sandbox".
 * 
 * @param {string} url - The Salesforce URL to check
 * @returns {boolean} True if the URL is a production org, false if sandbox or invalid
 */
export const isProductionSalesforceUrl = (url: string): boolean => {
    if (!url || typeof url !== CQAppConstant.DATA_TYPES.STRING) {
        return false;
    }
    
    // Validate URL format
    try {
        new URL(url);
    } catch {
        return false;
    }
    
    // Check if URL contains "sandbox" (case-insensitive)
    const lowerUrl = url.toLowerCase();
    // Standard sandbox login URL
    if (lowerUrl.includes(CQAppConstant.SANDBOX_LOGIN_DOMAIN)) return false;
    // Custom domain sandbox check
    return !lowerUrl.includes(`.${CQAppConstant.ENV_KEYWORDS.SANDBOX}.`);
};

/**
 * Validates if a URL string is properly formatted.
 * 
 * @param {string} urlString - The URL string to validate.
 * @returns {boolean} True if the URL is valid, false otherwise.
 */
export const isValidUrl = (urlString: string): boolean => {
    try {
        new URL(urlString);
        return true;
    } catch {
        return false;
    }
};
